import { MeetingStorage } from './meetingStorage';

/**
 * Physician Preference Analysis System
 * Analyzes past meeting data to provide intelligent recommendations
 */

export class PhysicianPreferences {
  /**
   * Get all meetings for a specific physician from real data
   */
  static getPhysicianMeetings(physicianId) {
    const allMeetings = MeetingStorage.getAllMeetings();
    return allMeetings.filter(meeting =>
      (meeting.customerId === physicianId || meeting.customer_id === physicianId) &&
      meeting.status === 'completed'
    );
  }

  /**
   * Analyze restaurant preferences based on order history
   */
  static analyzeRestaurantPreferences(physicianId) {
    const meetings = this.getPhysicianMeetings(physicianId);
    const restaurantStats = {};
    
    meetings.forEach(meeting => {
      if (meeting.restaurant && meeting.restaurant !== 'Not selected') {
        if (!restaurantStats[meeting.restaurant]) {
          restaurantStats[meeting.restaurant] = {
            count: 0,
            totalSpent: 0,
            lastVisit: null,
            avgRating: 0,
            restaurantId: meeting.restaurantId
          };
        }
        
        restaurantStats[meeting.restaurant].count++;
        restaurantStats[meeting.restaurant].totalSpent += parseFloat(meeting.orderTotal || 0);
        
        const meetingDate = new Date(meeting.date);
        if (!restaurantStats[meeting.restaurant].lastVisit || 
            meetingDate > new Date(restaurantStats[meeting.restaurant].lastVisit)) {
          restaurantStats[meeting.restaurant].lastVisit = meeting.date;
        }
      }
    });

    return restaurantStats;
  }

  /**
   * Get restaurant recommendations with reasoning
   */
  static getRestaurantRecommendations(physicianId, availableRestaurants) {
    const restaurantStats = this.analyzeRestaurantPreferences(physicianId);
    const recommendations = [];
    
    availableRestaurants.forEach(restaurant => {
      const stats = restaurantStats[restaurant.name];
      const recommendation = {
        restaurant,
        priority: 'medium',
        reason: 'Available option',
        insights: [],
        score: restaurant.rating || 0
      };

      if (stats) {
        const daysSinceLastVisit = stats.lastVisit ? 
          Math.floor((new Date() - new Date(stats.lastVisit)) / (1000 * 60 * 60 * 24)) : null;
        
        // High frequency restaurant
        if (stats.count >= 3) {
          recommendation.priority = 'high';
          recommendation.reason = `Frequently ordered (${stats.count} times)`;
          recommendation.insights.push(`Preferred choice - ordered ${stats.count} times`);
          recommendation.score += 2;
        }
        // Haven't been in a while
        else if (daysSinceLastVisit && daysSinceLastVisit > 60) {
          recommendation.priority = 'high';
          recommendation.reason = `Haven't visited in ${Math.floor(daysSinceLastVisit / 30)} months`;
          recommendation.insights.push(`Last visit: ${stats.lastVisit}`);
          recommendation.insights.push(`Might be ready for a return visit`);
          recommendation.score += 1.5;
        }
        // Recent visit
        else if (daysSinceLastVisit && daysSinceLastVisit < 14) {
          recommendation.priority = 'low';
          recommendation.reason = `Recently visited (${daysSinceLastVisit} days ago)`;
          recommendation.insights.push(`Last order: $${stats.totalSpent.toFixed(2)}`);
          recommendation.score -= 0.5;
        }
        // Good spending history
        else if (stats.totalSpent > 100) {
          recommendation.priority = 'medium-high';
          recommendation.reason = 'Solid order history';
          recommendation.insights.push(`Total spent: $${stats.totalSpent.toFixed(2)}`);
          recommendation.score += 1;
        }
      } else {
        // Never tried before
        recommendation.priority = 'medium-high';
        recommendation.reason = 'New option to try';
        recommendation.insights.push('Never ordered from here before');
        recommendation.insights.push('Good opportunity to explore preferences');
        recommendation.score += 0.5;
      }

      recommendations.push(recommendation);
    });

    // Sort by score (highest first)
    return recommendations.sort((a, b) => b.score - a.score);
  }

  /**
   * Analyze order patterns and preferences
   */
  static analyzeOrderPatterns(physicianId) {
    const meetings = this.getPhysicianMeetings(physicianId);
    const orderAnalysis = {
      averageSpend: 0,
      favoriteItems: {},
      spendingTrend: 'stable',
      lastOrderItems: [],
      totalMeetings: meetings.length
    };

    let totalSpent = 0;
    const allItems = [];

    meetings.forEach(meeting => {
      if (meeting.orderItems && Array.isArray(meeting.orderItems)) {
        totalSpent += parseFloat(meeting.orderTotal || 0);
        
        meeting.orderItems.forEach(item => {
          allItems.push(item);
          
          if (!orderAnalysis.favoriteItems[item.name]) {
            orderAnalysis.favoriteItems[item.name] = {
              count: 0,
              totalSpent: 0,
              averagePrice: 0
            };
          }
          
          orderAnalysis.favoriteItems[item.name].count += item.quantity || 1;
          orderAnalysis.favoriteItems[item.name].totalSpent += 
            parseFloat(item.price) * (item.quantity || 1);
        });

        // Store last order items
        if (meeting.date === Math.max(...meetings.map(m => new Date(m.date)))) {
          orderAnalysis.lastOrderItems = meeting.orderItems;
        }
      }
    });

    orderAnalysis.averageSpend = meetings.length > 0 ? totalSpent / meetings.length : 0;

    // Calculate average prices for favorite items
    Object.keys(orderAnalysis.favoriteItems).forEach(itemName => {
      const item = orderAnalysis.favoriteItems[itemName];
      item.averagePrice = item.totalSpent / item.count;
    });

    return orderAnalysis;
  }

  /**
   * Get time preferences based on past meetings
   */
  static analyzeTimePreferences(physicianId) {
    const meetings = this.getPhysicianMeetings(physicianId);
    const timeSlots = {};
    
    meetings.forEach(meeting => {
      if (meeting.time) {
        if (!timeSlots[meeting.time]) {
          timeSlots[meeting.time] = 0;
        }
        timeSlots[meeting.time]++;
      }
    });

    const sortedTimes = Object.entries(timeSlots)
      .sort(([,a], [,b]) => b - a)
      .map(([time, count]) => ({ time, count }));

    return {
      preferredTimes: sortedTimes,
      mostCommonTime: sortedTimes[0]?.time,
      totalMeetings: meetings.length
    };
  }

  /**
   * Generate complete preference insights for booking flow
   */
  static getBookingInsights(physicianId, availableRestaurants) {
    const restaurantRecommendations = this.getRestaurantRecommendations(physicianId, availableRestaurants);
    const orderPatterns = this.analyzeOrderPatterns(physicianId);
    const timePreferences = this.analyzeTimePreferences(physicianId);

    return {
      restaurantRecommendations,
      orderPatterns,
      timePreferences,
      summary: {
        totalMeetings: orderPatterns.totalMeetings,
        averageSpend: orderPatterns.averageSpend,
        preferredTime: timePreferences.mostCommonTime,
        topRestaurant: restaurantRecommendations[0]
      }
    };
  }

  /**
   * Get quick reorder suggestions based on last order
   */
  static getReorderSuggestions(physicianId) {
    const meetings = this.getPhysicianMeetings(physicianId);
    if (meetings.length === 0) return null;

    // Find most recent completed meeting
    const recentMeeting = meetings
      .sort((a, b) => new Date(b.date) - new Date(a.date))[0];

    if (!recentMeeting.orderItems) return null;

    const totalCost = recentMeeting.orderItems
      .reduce((sum, item) => sum + (parseFloat(item.price) * (item.quantity || 1)), 0);

    return {
      restaurant: recentMeeting.restaurant,
      restaurantId: recentMeeting.restaurantId,
      items: recentMeeting.orderItems,
      totalCost: totalCost.toFixed(2),
      date: recentMeeting.date,
      note: `Last order from ${new Date(recentMeeting.date).toLocaleDateString()}`
    };
  }
}

export default PhysicianPreferences;